import React from "react";
import moment from "moment";
import * as Icon from "react-feather";

import WComponent from "../common/WComponent";
import ToolsSelect from "../clients/Tools/kit/ToolsSelect";
import ToolsTabs from "../clients/Tools/kit/ToolsTabs";
import ToolsRowMenu from "../clients/Tools/kit/ToolsRowMenu";
import ToolsClientSearch from "../clients/Tools/kit/ToolsClientSearch";
import PlaybookLibraryService from "../../services/PlaybookLibraryService";
import PlaybookRuleService from "../../services/PlaybookRuleService";
import UtilityService from "../../services/UtilityService";

import "./agent-rules-panel.css";

const EMPTY_RULE_FORM = { scopeKind: "firm", playbookId: "", client: null, text: "" };
const EMPTY_FACT_FORM = { client: null, text: "" };
const LISTS = { rules: "rules", facts: "facts" };

class AgentRulesPanel extends WComponent {
	constructor(props) {
		super(props);
		this.state = {
			rules: [],
			facts: [],
			isLoading: true,
			loadError: null,
			ruleForm: { ...EMPTY_RULE_FORM },
			factForm: { ...EMPTY_FACT_FORM },
			isSaving: false,
			saveError: null,
			openList: LISTS.rules,
		};
	}

	componentDidMount() {
		this.fetchEverything();
	}

	fetchEverything = async () => {
		try {
			const [rules, facts] = await Promise.all([PlaybookRuleService.listRules(), PlaybookRuleService.listFacts()]);
			await this.update({ rules, facts, isLoading: false, loadError: null });
		} catch (error) {
			await this.update({ isLoading: false, loadError: UtilityService.describeRequestError({ error, fallback: "The rules could not be loaded." }) });
		}
	};

	onClientCleared = ({ target }) => {
		const { ruleForm, factForm } = this.state;
		const patch = target === "fact" ? { factForm: { ...factForm, client: null } } : { ruleForm: { ...ruleForm, client: null } };
		this.update(patch);
	};

	onClientChosen = ({ target, client }) => {
		const { ruleForm, factForm } = this.state;
		const patch = target === "fact" ? { factForm: { ...factForm, client } } : { ruleForm: { ...ruleForm, client } };
		this.update(patch);
	};

	onSaveRule = async () => {
		const { ruleForm } = this.state;
		const scopeId =
			ruleForm.scopeKind === "playbook" ? Number(ruleForm.playbookId) : ruleForm.scopeKind === "client" && ruleForm.client ? ruleForm.client.id : null;
		await this.update({ isSaving: true, saveError: null });
		try {
			await PlaybookRuleService.createRule({ scopeKind: ruleForm.scopeKind, scopeId, text: ruleForm.text.trim() });
			await this.update({ isSaving: false, ruleForm: { ...EMPTY_RULE_FORM }, openList: LISTS.rules });
			await this.fetchEverything();
		} catch (error) {
			await this.update({ isSaving: false, saveError: UtilityService.describeRequestError({ error, fallback: "The rule was not saved." }) });
		}
	};

	onSaveFact = async () => {
		const { factForm } = this.state;
		await this.update({ isSaving: true, saveError: null });
		try {
			await PlaybookRuleService.confirmFact({ clientId: factForm.client.id, text: factForm.text.trim() });
			await this.update({ isSaving: false, factForm: { ...EMPTY_FACT_FORM }, openList: LISTS.facts });
			await this.fetchEverything();
		} catch (error) {
			await this.update({ isSaving: false, saveError: UtilityService.describeRequestError({ error, fallback: "The fact was not saved." }) });
		}
	};

	onRetireRule = async ({ ruleId }) => {
		await this.update({ saveError: null });
		try {
			await PlaybookRuleService.retireRule({ ruleId });
			await this.fetchEverything();
		} catch (error) {
			await this.update({ saveError: UtilityService.describeRequestError({ error, fallback: "The rule was not retired." }) });
		}
	};

	onRetireFact = async ({ factId }) => {
		await this.update({ saveError: null });
		try {
			await PlaybookRuleService.retireFact({ factId });
			await this.fetchEverything();
		} catch (error) {
			await this.update({ saveError: UtilityService.describeRequestError({ error, fallback: "The fact was not retired." }) });
		}
	};

	renderClientPicker({ target, chosen }) {
		const { isSaving } = this.state;
		return (
			<ToolsClientSearch
				chosen={chosen}
				onChoose={({ client }) => this.onClientChosen({ target, client })}
				onClear={() => this.onClientCleared({ target })}
				placeholder="Find the client…"
				disabled={isSaving}
			/>
		);
	}

	renderRuleForm() {
		const { playbooks } = this.props;
		const { ruleForm, isSaving } = this.state;
		const playbookChoices = PlaybookLibraryService.playbookChoices({ playbooks });
		const hasScope =
			ruleForm.scopeKind === "firm" || (ruleForm.scopeKind === "playbook" && ruleForm.playbookId) || (ruleForm.scopeKind === "client" && ruleForm.client);
		const isReady = hasScope && ruleForm.text.trim().length > 0;

		return (
			<section className="tk-card agent-rules__form">
				<div className="agent-rules__form-head">
					<span className="tk-icon-tile tk-icon-tile--sm">
						<Icon.Shield />
					</span>
					<div>
						<div className="agent-rules__form-title">Set a rule</div>
						<div className="agent-rules__form-hint">A standing instruction every run in its scope follows.</div>
					</div>
				</div>
				<div className="tk-form-field">
					<div className="tk-form-label">Applies to</div>
					<div className="agent-rules__scope-row">
						<ToolsSelect
							value={ruleForm.scopeKind}
							options={PlaybookRuleService.ruleScopeChoices()}
							onChange={scopeKind => this.update({ ruleForm: { ...ruleForm, scopeKind, playbookId: "", client: null } })}
						/>
						{ruleForm.scopeKind === "playbook" && (
							<ToolsSelect
								value={ruleForm.playbookId}
								options={playbookChoices}
								onChange={playbookId => this.update({ ruleForm: { ...ruleForm, playbookId } })}
								placeholder="Choose the playbook…"
							/>
						)}
						{ruleForm.scopeKind === "client" && this.renderClientPicker({ target: "rule", chosen: ruleForm.client })}
					</div>
				</div>
				<div className="tk-form-field">
					<div className="tk-form-label">The rule</div>
					<textarea
						className="tk-input agent-rules__text"
						rows={2}
						value={ruleForm.text}
						onChange={event => this.update({ ruleForm: { ...ruleForm, text: event.target.value } })}
						placeholder="In plain words, e.g. Never raise balances under $100 on petty cash."
						maxLength={1000}
						disabled={isSaving}
					/>
				</div>
				<div className="agent-rules__form-actions">
					<button type="button" className="tk-button tk-button--primary tk-button--compact" onClick={this.onSaveRule} disabled={!isReady || isSaving}>
						<Icon.Plus size={13} />
						Set the rule
					</button>
				</div>
			</section>
		);
	}

	renderFactForm() {
		const { factForm, isSaving } = this.state;
		const isReady = !!factForm.client && factForm.text.trim().length > 0;

		return (
			<section className="tk-card agent-rules__form">
				<div className="agent-rules__form-head">
					<span className="tk-icon-tile tk-icon-tile--sm tk-icon-tile--success">
						<Icon.CheckCircle />
					</span>
					<div>
						<div className="agent-rules__form-title">Confirm a fact about a client</div>
						<div className="agent-rules__form-hint">Something every run on that client should know before it starts.</div>
					</div>
				</div>
				<div className="tk-form-field">
					<div className="tk-form-label">Client</div>
					{this.renderClientPicker({ target: "fact", chosen: factForm.client })}
				</div>
				<div className="tk-form-field">
					<div className="tk-form-label">The fact</div>
					<textarea
						className="tk-input agent-rules__text"
						rows={2}
						value={factForm.text}
						onChange={event => this.update({ factForm: { ...factForm, text: event.target.value } })}
						placeholder="e.g. 'Jane Transit' is a clearing account."
						maxLength={1000}
						disabled={isSaving}
					/>
				</div>
				<div className="agent-rules__form-actions">
					<button type="button" className="tk-button tk-button--primary tk-button--compact" onClick={this.onSaveFact} disabled={!isReady || isSaving}>
						<Icon.Plus size={13} />
						Confirm the fact
					</button>
				</div>
			</section>
		);
	}

	renderRuleRow = rule => (
		<div key={rule.id} className="tk-table__row tk-table__row--body agent-rules__rule-row">
			<span className="agent-rules__rule-text">
				{rule.text}
				{rule.fromDecisionId && <span className="tk-badge tk-badge--neutral">From a rejection</span>}
			</span>
			<span className="tk-table__truncate">{rule.scopeName}</span>
			<span className="tk-table__truncate">{rule.createdBy}</span>
			<span className="tk-table__numeric tk-table__meta">{moment(rule.createdAt).format("MMM D, YYYY")}</span>
			<span className="tk-table__actions">
				<ToolsRowMenu items={[{ label: "Retire this rule", onClick: () => this.onRetireRule({ ruleId: rule.id }), danger: true }]} />
			</span>
		</div>
	);

	renderFactRow = fact => (
		<div key={fact.id} className="tk-table__row tk-table__row--body agent-rules__fact-row">
			<span className="agent-rules__rule-text">
				{fact.text}
				{fact.sourceRunId && <span className="tk-badge tk-badge--neutral">From run #{fact.sourceRunId}</span>}
			</span>
			<span className="tk-table__truncate">{fact.clientName}</span>
			<span className="tk-table__truncate">{fact.confirmedBy}</span>
			<span className="tk-table__numeric tk-table__meta">{moment(fact.confirmedAt).format("MMM D, YYYY")}</span>
			<span className="tk-table__actions">
				<ToolsRowMenu items={[{ label: "Retire this fact", onClick: () => this.onRetireFact({ factId: fact.id }), danger: true }]} />
			</span>
		</div>
	);

	renderRulesTable() {
		const { rules } = this.state;
		if (rules.length === 0) return <div className="tk-empty tk-card">No rules yet. Set one above, or make one from a rejection on a run.</div>;
		return (
			<div className="tk-table tk-table--dense tk-card">
				<div className="tk-table__row tk-table__row--head agent-rules__rule-row">
					<span className="tk-table__head-cell">Rule</span>
					<span className="tk-table__head-cell">Applies to</span>
					<span className="tk-table__head-cell">Set by</span>
					<span className="tk-table__head-cell">Since</span>
					<span className="tk-table__head-cell" aria-hidden="true" />
				</div>
				{rules.map(this.renderRuleRow)}
			</div>
		);
	}

	renderFactsTable() {
		const { facts } = this.state;
		if (facts.length === 0) return <div className="tk-empty tk-card">No facts yet. Confirm one above.</div>;
		return (
			<div className="tk-table tk-table--dense tk-card">
				<div className="tk-table__row tk-table__row--head agent-rules__fact-row">
					<span className="tk-table__head-cell">Fact</span>
					<span className="tk-table__head-cell">Client</span>
					<span className="tk-table__head-cell">Confirmed by</span>
					<span className="tk-table__head-cell">Since</span>
					<span className="tk-table__head-cell" aria-hidden="true" />
				</div>
				{facts.map(this.renderFactRow)}
			</div>
		);
	}

	render() {
		const { rules, facts, isLoading, loadError, saveError, openList } = this.state;
		const listTabs = [
			{ key: LISTS.rules, label: "Rules", count: rules.length },
			{ key: LISTS.facts, label: "Client facts", count: facts.length },
		];

		return (
			<div className="agent-rules">
				<p className="tk-page-intro">
					Rules and confirmed facts are handed to every run they apply to before it starts, and a run says when one changed what it did. A rejection
					on a run can become a rule in one click.
				</p>
				{loadError && <div className="tk-notice tk-notice--danger">{loadError}</div>}
				{saveError && <div className="tk-notice tk-notice--danger">{saveError}</div>}
				<div className="agent-rules__forms">
					{this.renderRuleForm()}
					{this.renderFactForm()}
				</div>
				{isLoading && <div className="tk-empty tk-card">Loading rules…</div>}
				{!isLoading && (
					<React.Fragment>
						<ToolsTabs subordinate tabs={listTabs} activeKey={openList} onSelect={value => this.update({ openList: value })} />
						{openList === LISTS.rules ? this.renderRulesTable() : this.renderFactsTable()}
					</React.Fragment>
				)}
			</div>
		);
	}
}

export default AgentRulesPanel;
